/**
 * Config suggestions panel — phase 89.
 *
 * Renders the chat-config optimizer's proposed changes (prompt text / model
 * routing / tool allowlist) for staff approval. Backed by
 * `GET /api/efficiency/suggestions`; Approve / Reject / Revert dispatch the
 * `apply_prompt_suggestion` / `reject_prompt_suggestion` /
 * `revert_prompt_suggestion` canonical actions (as `human:staff`) — the SAME
 * path a WhatsApp APPROVE takes, so both surfaces converge on one row.
 *
 * Used both as a section on `/efficiency` and standalone at
 * `/efficiency/suggestions` (the WhatsApp nudge's deep link).
 */

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  ApiError,
  type ChatConfigSuggestionView,
  type ChatConfigSuggestionChange,
  type ChatConfigSuggestionOutcomeSnapshot,
  type ChatConfigSuggestionsResponse,
} from "../lib/api";
import { useDispatch } from "../lib/use-dispatch";
import { studioTimeZone } from "../lib/studio-tz";

function fieldLabel(field: string): string {
  return field.replace(/^chat_translator\./, "");
}

function asLines(v: string | ReadonlyArray<string> | null): string {
  if (v === null) return "(no override — code default)";
  if (Array.isArray(v)) return v.join("\n");
  return String(v);
}

export type DiffLine = { readonly kind: "same" | "add" | "del"; readonly text: string };

/** Compact LCS line-diff (no dependency) for the before/after prompt text.
 *  Stays exported: the /evals PreloadSuggestions panel that consumed it was
 *  retired 2026-08-04 (d7c697dc), but the export is this page's own diff
 *  primitive and future panels may reuse it. */
export function lineDiff(before: string, after: string): ReadonlyArray<DiffLine> {
  const a = before.split("\n");
  const b = after.split("\n");
  const n = a.length;
  const m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      out.push({ kind: "del", text: a[i]! });
      i++;
    } else {
      out.push({ kind: "add", text: b[j]! });
      j++;
    }
  }
  while (i < n) out.push({ kind: "del", text: a[i++]! });
  while (j < m) out.push({ kind: "add", text: b[j++]! });
  return out;
}

function ChangeView({ change }: { change: ChatConfigSuggestionChange }): JSX.Element {
  const label = fieldLabel(change.target_field);
  if (change.lever === "tool_allowlist") {
    const before = new Set(Array.isArray(change.before_value) ? change.before_value : []);
    const after = new Set(Array.isArray(change.after_value) ? change.after_value : []);
    const all = [...new Set([...before, ...after])].sort();
    return (
      <div style={{ marginTop: 8 }}>
        <strong>{label}</strong> (tool allowlist)
        <div style={{ marginTop: 4 }}>
          {all.map((t) => {
            const removed = before.has(t) && !after.has(t);
            const added = !before.has(t) && after.has(t);
            return (
              <span
                key={t}
                className="tag"
                style={{
                  marginRight: 4,
                  marginBottom: 4,
                  display: "inline-block",
                  background: added ? "#d6efdf" : removed ? "#f8dde1" : "#eae8e4",
                  textDecoration: removed ? "line-through" : "none",
                  color: added ? "#16693a" : removed ? "#8a1024" : "#33363d",
                }}
              >
                {t}
              </span>
            );
          })}
        </div>
      </div>
    );
  }
  if (change.lever === "model_routing") {
    return (
      <div style={{ marginTop: 8 }}>
        <strong>{label}</strong> (model):{" "}
        <span className="subtle">{asLines(change.before_value)}</span>
        {" → "}
        <span style={{ color: "#16693a" }}>{asLines(change.after_value)}</span>
      </div>
    );
  }
  // prompt_text — line diff.
  const diff = lineDiff(asLines(change.before_value), asLines(change.after_value));
  return (
    <div style={{ marginTop: 8 }}>
      <strong>{label}</strong> (prompt text)
      <pre
        style={{
          marginTop: 4,
          maxHeight: 280,
          overflow: "auto",
          fontSize: 12,
          lineHeight: 1.4,
          border: "1px solid #e2e8f0",
          borderRadius: 4,
          padding: 8,
          whiteSpace: "pre-wrap",
        }}
      >
        {diff.map((l, idx) => (
          <div
            key={idx}
            style={{
              background:
                l.kind === "add" ? "#d6efdf" : l.kind === "del" ? "#f8dde1" : "transparent",
              color: l.kind === "del" ? "#8a1024" : l.kind === "add" ? "#16693a" : "inherit",
            }}
          >
            {l.kind === "add" ? "+ " : l.kind === "del" ? "- " : "  "}
            {l.text}
          </div>
        ))}
      </pre>
    </div>
  );
}

function EvalBadge({ s }: { s: ChatConfigSuggestionView }): JSX.Element {
  if (s.eval_passed === null) {
    return <span className="tag" style={{ background: "#eae8e4" }}>eval: not gated</span>;
  }
  const pct = s.eval_pass_rate !== null ? Math.round(s.eval_pass_rate * 100) : null;
  const base =
    s.eval_baseline_rate !== null ? ` (was ${Math.round(s.eval_baseline_rate * 100)}%)` : "";
  if (s.eval_passed) {
    return (
      <span className="tag" style={{ background: "#d6efdf", color: "#16693a" }}>
        eval {pct}% pass{base}
      </span>
    );
  }
  return (
    <span className="tag tag-danger">
      eval {pct}% REGRESSED{base} — apply blocked
    </span>
  );
}

/** Format one signed delta (e.g. `+0.4`, `−0.3`) or `—` when unmeasured. */
function signedDelta(v: number | null): string {
  if (v === null) return "—";
  const rounded = Math.round(v * 10) / 10;
  if (rounded === 0) return "0.0";
  const sign = rounded > 0 ? "+" : "−"; // U+2212 minus for the negative case
  return `${sign}${Math.abs(rounded).toFixed(1)}`;
}

/** Green for a positive delta, red for negative, muted for flat/unmeasured. */
function deltaColour(v: number | null): string {
  if (v === null || Math.round(v * 10) / 10 === 0) return "#5c6069";
  return v > 0 ? "#16693a" : "#8a1024";
}

/**
 * Migration-076 post-apply outcome cell — the loop's back-half made visible.
 *   - null snapshot → muted `—` (not yet measured / pre-076 row).
 *   - measured but not `sufficient` → muted `measuring… (n=<after.n>)`.
 *   - `sufficient` → `truthfulness +0.4 · corr +0.3 (n=18, 5d)` with each
 *     signed delta coloured green (up) / red (down).
 */
function OutcomeCell({
  outcome,
}: {
  outcome: ChatConfigSuggestionOutcomeSnapshot | null;
}): JSX.Element {
  if (outcome === null) return <span className="subtle">—</span>;
  if (!outcome.sufficient) {
    return (
      <span className="subtle" title="Not enough scored requests yet to compare">
        measuring… (n={outcome.after.n})
      </span>
    );
  }
  const parts: ReadonlyArray<{ label: string; v: number | null }> = [
    { label: "truthfulness", v: outcome.delta.truthfulness },
    { label: "corr", v: outcome.delta.correctness },
    { label: "goal", v: outcome.delta.goal_completion },
  ];
  return (
    <span
      title={`before/after over ${outcome.window_days}d, ${outcome.horizon_days}d horizon (n=${outcome.after.n})`}
    >
      {parts.map((p, i) => (
        <Fragment key={p.label}>
          {i > 0 ? <span className="subtle"> · </span> : null}
          {p.label} <span style={{ color: deltaColour(p.v) }}>{signedDelta(p.v)}</span>
        </Fragment>
      ))}
      <span className="subtle">
        {" "}
        (n={outcome.after.n}, {outcome.window_days}d)
      </span>
    </span>
  );
}

export function ConfigSuggestions(): JSX.Element {
  const [data, setData] = useState<ChatConfigSuggestionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Which history rows have their before/after change expanded.
  const [expandedHistory, setExpandedHistory] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const cancelledRef = useRef(false);

  const toggleHistory = useCallback((id: string): void => {
    setExpandedHistory((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const apply = useDispatch<
    { suggestion_id: string; override_eval_gate?: boolean },
    unknown
  >("apply_prompt_suggestion");
  const reject = useDispatch<{ suggestion_id: string }, unknown>("reject_prompt_suggestion");
  const revert = useDispatch<{ suggestion_id: string }, unknown>("revert_prompt_suggestion");

  const load = useCallback(async (): Promise<void> => {
    try {
      const r = await api.chatConfigSuggestions();
      if (cancelledRef.current) return;
      setData(r);
      setError(null);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof ApiError ? e.code ?? `http_${e.status}` : (e as Error).message);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    void load();
    return () => {
      cancelledRef.current = true;
    };
  }, [load]);

  const onApply = useCallback(
    async (id: string) => {
      await apply.run({ suggestion_id: id });
      await load();
    },
    [apply, load],
  );
  // Eval-gate override — dispatches the SAME apply action with
  // override_eval_gate:true, which the server admits ONLY from a non-WhatsApp
  // staff surface (this dashboard). Shown only for eval-REGRESSED rows;
  // confirm() so it can't fire by accident.
  const onApplyAnyway = useCallback(
    async (id: string, ref: string) => {
      const ok = window.confirm(
        `Apply suggestion ${ref} ANYWAY?\n\nThis change REGRESSED the eval — applying it may make the chat agent worse. Only do this if you're confident the eval is wrong for this case.`,
      );
      if (!ok) return;
      await apply.run({ suggestion_id: id, override_eval_gate: true });
      await load();
    },
    [apply, load],
  );
  const onReject = useCallback(
    async (id: string) => {
      await reject.run({ suggestion_id: id });
      await load();
    },
    [reject, load],
  );
  const onRevert = useCallback(
    async (id: string) => {
      await revert.run({ suggestion_id: id });
      await load();
    },
    [revert, load],
  );

  if (loading && data === null) return <p className="empty">Loading suggestions…</p>;
  if (error !== null && data === null)
    return <p className="empty">Couldn&apos;t load suggestions ({error}).</p>;
  if (data === null) return <p className="empty">No data.</p>;

  const busy = apply.loading || reject.loading || revert.loading;
  const dispatchError = apply.error ?? reject.error ?? revert.error;

  return (
    <section className="panel">
      <h2>Config suggestions</h2>
      <p className="member-note">
        Daily proposals from the <code>chat_config_optimizer_daily</code> optimizer to improve
        the chat agent&apos;s prompt, model, or tool allowlist. Approve to apply live (no
        redeploy); revert any time.
      </p>
      {dispatchError !== null ? (
        <p className="tag tag-danger" style={{ display: "inline-block", marginBottom: 8 }}>
          {/* dispatchError.message already carries the `code: ` prefix (DispatchError
              composes `${code}: ${message}`), so render it alone — prefixing with
              `code` again produced the "stale: stale: stale:" triple. */}
          {dispatchError.message}
        </p>
      ) : null}

      {data.pending.length === 0 ? (
        <p className="empty">
          No pending suggestions. The optimizer runs daily and stages one here when it finds a
          high-confidence improvement.
        </p>
      ) : (
        data.pending.map((s) => (
          <div
            key={s.id}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>
                {s.ref}{" "}
                <span className="subtle">· priority {s.priority}</span>
              </strong>
              <EvalBadge s={s} />
            </div>
            <p style={{ marginTop: 6 }}>{s.rationale}</p>
            {s.changes.map((c, i) => (
              <ChangeView key={i} change={c} />
            ))}
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                disabled={busy || s.eval_passed === false}
                title={
                  s.eval_passed === false
                    ? "Apply is blocked — this change regressed the eval"
                    : "Apply this change to the live config"
                }
                onClick={() => void onApply(s.id)}
                style={{
                  border: "1px solid #16a34a",
                  color: "#fff",
                  background: s.eval_passed === false ? "#90939a" : "#22975a",
                  borderRadius: 4,
                  padding: "4px 14px",
                  marginRight: 8,
                  cursor: busy || s.eval_passed === false ? "not-allowed" : "pointer",
                  font: "inherit",
                }}
              >
                Approve
              </button>
              {s.eval_passed === false ? (
                <button
                  type="button"
                  disabled={busy}
                  title="Apply despite the eval regression — dashboard-only override"
                  onClick={() => void onApplyAnyway(s.id, s.ref)}
                  style={{
                    border: "1px solid #b91c1c",
                    color: "#fff",
                    background: "#a2142a",
                    borderRadius: 4,
                    padding: "4px 14px",
                    marginRight: 8,
                    cursor: busy ? "wait" : "pointer",
                    font: "inherit",
                  }}
                >
                  Apply anyway (eval regressed)
                </button>
              ) : null}
              <button
                type="button"
                disabled={busy}
                onClick={() => void onReject(s.id)}
                style={{
                  border: "1px solid #dc2626",
                  color: "#c0182f",
                  background: "none",
                  borderRadius: 4,
                  padding: "4px 14px",
                  cursor: busy ? "wait" : "pointer",
                  font: "inherit",
                }}
              >
                Reject
              </button>
            </div>
          </div>
        ))
      )}

      {data.history.length > 0 ? (
        <>
          <h3 style={{ marginTop: 16 }}>History</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Status</th>
                <th>Levers</th>
                <th>When</th>
                <th>By</th>
                <th>Effect</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((s) => {
                const open = expandedHistory.has(s.id);
                return (
                  <Fragment key={s.id}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          onClick={() => toggleHistory(s.id)}
                          aria-expanded={open}
                          title={open ? "Hide change" : "Show before / after"}
                          style={{
                            border: "none",
                            background: "none",
                            font: "inherit",
                            color: "#1B33C0",
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          {open ? "▾ " : "▸ "}
                          {s.ref}
                        </button>
                      </td>
                      <td>{s.status === "no_change" ? "no change" : s.status}</td>
                      <td className="subtle">
                        {s.changes.length === 0
                          ? "—"
                          : [...new Set(s.changes.map((c) => c.lever))].join(", ")}
                      </td>
                      <td>
                        {new Date(s.applied_at ?? s.created_at).toLocaleString(
                          "en-GB",
                          { timeZone: studioTimeZone() },
                        )}
                      </td>
                      <td className="subtle">{s.applied_by ?? "—"}</td>
                      <td>
                        <OutcomeCell outcome={s.outcome_snapshot} />
                      </td>
                      <td>
                        {s.status === "applied" ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void onRevert(s.id)}
                            style={{
                              border: "1px solid #2563eb",
                              color: "#1B33C0",
                              background: "none",
                              borderRadius: 4,
                              padding: "2px 10px",
                              cursor: busy ? "wait" : "pointer",
                              font: "inherit",
                            }}
                          >
                            Revert
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {open ? (
                      <tr>
                        <td colSpan={7} style={{ background: "#f7f6f3" }}>
                          <div style={{ padding: "4px 8px 12px" }}>
                            <p className="subtle" style={{ margin: "4px 0" }}>
                              {s.rationale}
                            </p>
                            {s.changes.map((c, i) => (
                              <ChangeView key={i} change={c} />
                            ))}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      ) : null}
    </section>
  );
}
